import React, { useMemo } from "react";
import { Form, Input, Row, Col, FormGroup, Label } from "reactstrap";
import { useTranslation } from "react-i18next";
import Select from "components/Select";
import { getActiveColorStyle } from "utils/colorUtils";
import {
  getTheme2MultiSelectSelectConfig,
  getTheme2SearchFilterColProps,
  getTheme2SearchFilterMultiSelectStyles,
  getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";

const SEARCH_ACCOUNTS_STATUS_PREFIX = "search-accounts-status";
const SEARCH_ACCOUNTS_LOCATION_PREFIX = "search-accounts-location";

const AccountsFilters = ({
  activeColor = "blue",
  isTheme2 = false,
  search,
  onSearchChange,
  status,
  onStatusChange,
  accountType,
  onAccountTypeChange,
  locations,
  onLocationsChange,
  statusOptions = [],
  accountTypeOptions = [],
  locationsData,
  showAccountTypeFilter = true,
  showLocationFilters = true,
  showTenantFilter = false,
  onTenantChange,
  tenantValue = null,
  tenantOptions = [],
  isLoadingTenants = false,
  tenantLabel = "Tenant",
}) => {
  const { t } = useTranslation();
  const activeColorStyle = useMemo(
    () => getActiveColorStyle(activeColor),
    [activeColor]
  );
  const statusMultiSelectStyles = useMemo(
    () => getTheme2SearchFilterMultiSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const filterSelectStyles = useMemo(
    () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const locationMultiSelectStyles = useMemo(
    () => getTheme2SearchFilterMultiSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const statusTheme2Config = useMemo(
    () => getTheme2MultiSelectSelectConfig(SEARCH_ACCOUNTS_STATUS_PREFIX, isTheme2),
    [isTheme2]
  );
  const locationTheme2Config = useMemo(
    () => getTheme2MultiSelectSelectConfig(SEARCH_ACCOUNTS_LOCATION_PREFIX, isTheme2),
    [isTheme2]
  );

  const filterColProps = getTheme2SearchFilterColProps(isTheme2, {
    filterColClass: "search-accounts-filter-col",
    md: "3",
  });
  const statusFilterColProps = isTheme2
    ? {
        ...filterColProps,
        className: `${filterColProps.className} search-accounts-filter-col--status`,
      }
    : filterColProps;
  const locationFilterColProps = isTheme2
    ? {
        ...filterColProps,
        className: `${filterColProps.className} search-accounts-filter-col--location`,
      }
    : filterColProps;

  const locationOptions =
    locationsData?.results?.map((location) => ({
      value: String(location.id),
      label: location.name,
    })) || [];

  return (
    <Form>
      <Row className={isTheme2 ? "search-accounts-filters-row" : undefined}>
        <Col {...filterColProps}>
          <FormGroup>
            <Label>{t("accounts.search")}</Label>
            <Input
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={t("accounts.searchPlaceholder")}
              className="form-control"
            />
          </FormGroup>
        </Col>
        <Col {...statusFilterColProps}>
          <FormGroup>
            <Label>{t("accounts.status")}</Label>
            <Select
              activeColor={activeColor}
              className={statusTheme2Config.controlClassName}
              placeholder={t("accounts.selectStatus")}
              name="statusSelect"
              isSearchable
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={statusTheme2Config.hideSelectedOptions}
              value={status}
              onChange={(value) => onStatusChange(value || [])}
              options={statusOptions}
              isClearable
              styles={statusMultiSelectStyles}
              components={statusTheme2Config.components}
              classNames={statusTheme2Config.classNames}
            />
          </FormGroup>
        </Col>
        {showTenantFilter && typeof onTenantChange === "function" ? (
          <Col {...filterColProps}>
            <FormGroup>
              <Label>{tenantLabel}</Label>
              <Select
                activeColor={activeColor}
                className="react-select"
                styles={filterSelectStyles}
                placeholder={tenantLabel}
                name="tenant"
                isSearchable
                closeMenuOnSelect={true}
                value={tenantValue}
                onChange={(option) => onTenantChange(option?.value || "")}
                options={tenantOptions}
                isClearable
                isDisabled={isLoadingTenants}
              />
            </FormGroup>
          </Col>
        ) : null}
        {showAccountTypeFilter ? (
        <Col {...filterColProps}>
          <FormGroup>
            <Label>{t("accounts.dealType")}</Label>
            <Select
              activeColor={activeColor}
              className="react-select"
              styles={filterSelectStyles}
              placeholder={t("accounts.selectAccountType")}
              name="accountType"
              isSearchable={false}
              closeMenuOnSelect={true}
              value={accountType ? accountTypeOptions.find((opt) => opt.value === accountType) : null}
              onChange={(option) => onAccountTypeChange(option?.value || "")}
              options={accountTypeOptions}
              isClearable
            />
          </FormGroup>
        </Col>
        ) : null}
        {showLocationFilters ? (
        <>
        <Col {...locationFilterColProps}>
          <FormGroup>
            <Label>{t("searchInventory.location")}</Label>
            <Select
              activeColor={activeColor}
              className={locationTheme2Config.controlClassName}
              styles={locationMultiSelectStyles}
              placeholder={t("searchInventory.selectLocation")}
              name="location"
              isSearchable
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={locationTheme2Config.hideSelectedOptions}
              value={locations}
              onChange={(option) => onLocationsChange(option || [])}
              options={locationOptions}
              isClearable
              components={locationTheme2Config.components}
              classNames={locationTheme2Config.classNames}
            />
          </FormGroup>
        </Col>
        </>
        ) : null}
      </Row>
    </Form>
  );
};

export default AccountsFilters;
